/**
 * MessageTemplatesPage — admin-only management of the shared message
 * templates catalog. Reads and writes go through the backend so edits
 * survive across origins/devices and are visible to Bulk WhatsApp + Lead
 * composers (the consumer-side hookup ships in a follow-up PR).
 *
 * Actions available to admin:
 *  - Filter by channel / stage / active-vs-archived.
 *  - Create custom template.
 *  - Edit body, name, stage and linked checklist key.
 *  - Toggle default per (channel, stage). Backend enforces uniqueness via
 *    a partial unique index; concurrent set-default returns 409.
 *  - Archive / unarchive.
 *  - Restore a builtin's original body from the seed catalog.
 *  - Run the idempotent seed (never fires from startup).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, PencilSimple, Archive, ArrowClockwise, CheckCircle, DownloadSimple, X,
} from "@phosphor-icons/react";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { formatError } from "@/lib/api";
import { STAGE_LABEL, STAGE_ORDER } from "@/lib/leadPlaybook";
import {
  listMessageTemplates,
  createMessageTemplate,
  updateMessageTemplate,
  archiveMessageTemplate,
  unarchiveMessageTemplate,
  setDefaultMessageTemplate,
  resetBuiltinMessageTemplate,
  seedMessageTemplates,
} from "@/lib/messageTemplatesApi";

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp" },
  { key: "email",    label: "Email" },
];
const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.key, c.label]));

const STAGE_OPTIONS = STAGE_ORDER.map((k) => ({ key: k, label: STAGE_LABEL[k] || k }));

function stageLabel(stage) {
  if (!stage) return "Sin fase";
  return STAGE_LABEL[stage] || stage;
}

function toneChip(tone) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    stone:   "bg-stone-100 text-stone-700 border-stone-200",
    blue:    "bg-blue-50 text-blue-800 border-blue-200",
    purple:  "bg-purple-50 text-purple-800 border-purple-200",
  };
  return `text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${map[tone] || map.stone}`;
}

export default function MessageTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Filters
  const [fChannel, setFChannel] = useState("");
  const [fStage, setFStage] = useState("");
  const [fIncludeArchived, setFIncludeArchived] = useState(true);

  // Editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // "create" | "edit"
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const rows = await listMessageTemplates({
        channel: fChannel || undefined,
        stage: fStage || undefined,
        includeArchived: fIncludeArchived,
      });
      setTemplates(rows);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [fChannel, fStage, fIncludeArchived]);

  useEffect(() => { reload(); }, [reload]);

  const visibleTemplates = useMemo(() => {
    // Backend already applies channel/stage/include_archived filters, so
    // frontend only sorts for a stable display order.
    return [...templates].sort((a, b) => {
      const ca = a.channel || ""; const cb = b.channel || "";
      if (ca !== cb) return ca.localeCompare(cb);
      const sa = a.stage || ""; const sb = b.stage || "";
      const ia = STAGE_ORDER.indexOf(sa); const ib = STAGE_ORDER.indexOf(sb);
      if (ia !== ib) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [templates]);

  const openCreate = () => {
    setEditorMode("create");
    setEditorTemplate({
      channel: "whatsapp",
      name: "",
      body: "",
      stage: null,
      linked_checklist_key: null,
    });
    setEditorOpen(true);
  };
  const openEdit = (tpl) => {
    setEditorMode("edit");
    setEditorTemplate({ ...tpl });
    setEditorOpen(true);
  };

  const runAction = async (id, fn, successMsg) => {
    setBusyId(id); setError(""); setNotice("");
    try {
      await fn();
      if (successMsg) setNotice(successMsg);
      await reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onSetDefault = (tpl) => runAction(
    tpl.template_id,
    () => setDefaultMessageTemplate(tpl.template_id),
    `"${tpl.name}" marcada como predeterminada para ${stageLabel(tpl.stage)}.`,
  );
  const onArchive = (tpl) => {
    if (!window.confirm(`¿Archivar la plantilla "${tpl.name}"? Se oculta sin borrar histórico.`)) return;
    runAction(
      tpl.template_id,
      () => archiveMessageTemplate(tpl.template_id),
      `"${tpl.name}" archivada.`,
    );
  };
  const onUnarchive = (tpl) => runAction(
    tpl.template_id,
    () => unarchiveMessageTemplate(tpl.template_id),
    `"${tpl.name}" restaurada.`,
  );
  const onResetBuiltin = (tpl) => {
    if (!window.confirm(`¿Restaurar el texto original de "${tpl.name}"? Se descartará la versión editada.`)) return;
    runAction(
      tpl.template_id,
      () => resetBuiltinMessageTemplate(tpl.template_id),
      `"${tpl.name}" restaurada al texto original.`,
    );
  };
  const onSeed = async () => {
    if (!window.confirm("¿Inicializar las plantillas predeterminadas? Se crearán únicamente las que no existan.")) return;
    setBusyId("seed"); setError(""); setNotice("");
    try {
      const res = await seedMessageTemplates();
      setNotice(`Seed ejecutado. Nuevas: ${res.created} · Ya existentes: ${res.existing}.`);
      await reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onEditorSave = async (patch) => {
    setError("");
    try {
      if (editorMode === "create") {
        const created = await createMessageTemplate({
          channel: patch.channel,
          name: patch.name.trim(),
          body: patch.body,
          stage: patch.stage || null,
          template_type: "custom",
          linked_checklist_key: patch.linked_checklist_key || null,
        });
        setNotice(`Plantilla "${created.name}" creada.`);
      } else {
        const updated = await updateMessageTemplate(editorTemplate.template_id, {
          name: patch.name.trim(),
          body: patch.body,
          stage: patch.stage || null,
          linked_checklist_key: patch.linked_checklist_key || null,
        });
        setNotice(`Plantilla "${updated.name}" actualizada.`);
      }
      setEditorOpen(false);
      setEditorTemplate(null);
      await reload();
    } catch (e) {
      setError(formatError(e));
    }
  };

  return (
    <AdminShell>
      <div className="mb-6 fade-up">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">CRM</div>
        <h1 className="font-serif-dv text-4xl">Plantillas de mensajes.</h1>
        <p className="text-stone-500 mt-2">
          Catálogo compartido de WhatsApp y email. Las ediciones afectan a todo el equipo y son la fuente que
          usarán el Bulk WhatsApp y el composer del lead.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <button
          type="button"
          onClick={openCreate}
          className="dv-btn dv-btn-primary text-sm inline-flex items-center gap-1.5"
          data-testid="msg-tpl-create"
        >
          <Plus size={14} /> Crear plantilla
        </button>
        <button
          type="button"
          onClick={onSeed}
          disabled={busyId === "seed"}
          className="dv-btn dv-btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="msg-tpl-seed"
          title="Crea únicamente las plantillas predeterminadas que aún no existen."
        >
          <DownloadSimple size={14} /> Inicializar plantillas predeterminadas
        </button>
      </div>

      <Section title="Catálogo" subtitle="Filtra por canal, fase o estado.">
        <div className="dv-card">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <label className="text-xs">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Canal</div>
              <select
                className="dv-input text-sm"
                value={fChannel}
                onChange={(e) => setFChannel(e.target.value)}
                data-testid="msg-tpl-filter-channel"
              >
                <option value="">Todos</option>
                {CHANNELS.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Fase</div>
              <select
                className="dv-input text-sm"
                value={fStage}
                onChange={(e) => setFStage(e.target.value)}
                data-testid="msg-tpl-filter-stage"
              >
                <option value="">Todas</option>
                {STAGE_OPTIONS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs inline-flex items-center gap-1.5 py-2">
              <input
                type="checkbox"
                checked={fIncludeArchived}
                onChange={(e) => setFIncludeArchived(e.target.checked)}
                data-testid="msg-tpl-filter-archived"
              />
              <span>Incluir archivadas</span>
            </label>
          </div>

          {error && (
            <div className="text-xs px-3 py-2 rounded border bg-red-50 border-red-200 text-red-700 mb-3" data-testid="msg-tpl-error">
              {error}
            </div>
          )}
          {notice && (
            <div className="text-xs px-3 py-2 rounded border bg-emerald-50 border-emerald-200 text-emerald-800 mb-3" data-testid="msg-tpl-notice">
              {notice}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-stone-500 py-6 text-center">Cargando…</div>
          ) : visibleTemplates.length === 0 ? (
            <div className="text-sm text-stone-500 py-6 text-center" data-testid="msg-tpl-empty">
              No hay plantillas para los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="msg-tpl-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                    <th className="text-left py-2 pr-2">Nombre</th>
                    <th className="text-left py-2 pr-2">Canal</th>
                    <th className="text-left py-2 pr-2">Fase</th>
                    <th className="text-left py-2 pr-2">Tipo</th>
                    <th className="text-left py-2 pr-2">Predeterminada</th>
                    <th className="text-left py-2 pr-2">Estado</th>
                    <th className="text-left py-2 pr-2">Actualizada</th>
                    <th className="text-right py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTemplates.map((tpl) => {
                    const isBusy = busyId === tpl.template_id;
                    const canBeDefault = tpl.is_active && !!tpl.stage && !tpl.is_default_for_stage;
                    const canReset = tpl.template_type === "builtin" && !!tpl.seed_key;
                    return (
                      <tr
                        key={tpl.template_id}
                        className={`border-b border-stone-100 ${tpl.is_active ? "" : "opacity-60"}`}
                        data-testid={`msg-tpl-row-${tpl.template_id}`}
                      >
                        <td className="py-2 pr-2 align-top">
                          <div className="font-medium">{tpl.name}</div>
                          <div className="text-xs text-stone-500 line-clamp-2 max-w-md whitespace-pre-line">{tpl.body}</div>
                        </td>
                        <td className="py-2 pr-2 align-top">{CHANNEL_LABEL[tpl.channel] || tpl.channel}</td>
                        <td className="py-2 pr-2 align-top">{stageLabel(tpl.stage)}</td>
                        <td className="py-2 pr-2 align-top">
                          <span className={toneChip(tpl.template_type === "builtin" ? "purple" : "blue")}>
                            {tpl.template_type === "builtin" ? "Builtin" : "Custom"}
                          </span>
                        </td>
                        <td className="py-2 pr-2 align-top">
                          {tpl.is_default_for_stage ? (
                            <span className={toneChip("emerald")}>Default</span>
                          ) : (
                            <span className="text-stone-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-2 align-top">
                          {tpl.is_active ? (
                            <span className={toneChip("stone")}>Activa</span>
                          ) : (
                            <span className={toneChip("amber")}>Archivada</span>
                          )}
                        </td>
                        <td className="py-2 pr-2 align-top text-xs text-stone-500 whitespace-nowrap">
                          {tpl.updated_at ? new Date(tpl.updated_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-2 align-top">
                          <div className="flex flex-wrap gap-1 justify-end">
                            <button
                              type="button"
                              onClick={() => openEdit(tpl)}
                              disabled={isBusy}
                              className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                              data-testid={`msg-tpl-edit-${tpl.template_id}`}
                              title="Editar nombre, body, fase, checklist"
                            >
                              <PencilSimple size={12} /> Editar
                            </button>
                            {canBeDefault && (
                              <button
                                type="button"
                                onClick={() => onSetDefault(tpl)}
                                disabled={isBusy}
                                className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                                data-testid={`msg-tpl-set-default-${tpl.template_id}`}
                                title="Marcar como predeterminada de esta fase"
                              >
                                <CheckCircle size={12} /> Default
                              </button>
                            )}
                            {tpl.is_active ? (
                              <button
                                type="button"
                                onClick={() => onArchive(tpl)}
                                disabled={isBusy}
                                className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                                data-testid={`msg-tpl-archive-${tpl.template_id}`}
                              >
                                <Archive size={12} /> Archivar
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onUnarchive(tpl)}
                                disabled={isBusy}
                                className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                                data-testid={`msg-tpl-unarchive-${tpl.template_id}`}
                              >
                                <ArrowClockwise size={12} /> Restaurar
                              </button>
                            )}
                            {canReset && (
                              <button
                                type="button"
                                onClick={() => onResetBuiltin(tpl)}
                                disabled={isBusy}
                                className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                                data-testid={`msg-tpl-reset-${tpl.template_id}`}
                                title="Restaurar body/nombre originales del catálogo seed"
                              >
                                <ArrowClockwise size={12} /> Restaurar original
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Section>

      {editorOpen && editorTemplate && (
        <TemplateEditor
          initial={editorTemplate}
          mode={editorMode}
          onClose={() => { setEditorOpen(false); setEditorTemplate(null); }}
          onSave={onEditorSave}
        />
      )}
    </AdminShell>
  );
}

function TemplateEditor({ initial, mode, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState("");

  const canEditChannel = mode === "create";
  const isBuiltin = initial.template_type === "builtin";

  const submit = async () => {
    setLocalError("");
    if (!form.name?.trim()) { setLocalError("El nombre es obligatorio."); return; }
    if (!form.body?.trim()) { setLocalError("El texto del mensaje es obligatorio."); return; }
    setSaving(true);
    try {
      await onSave(form);
    } catch (e) {
      setLocalError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white shadow-xl w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-xl sm:rounded-lg flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="msg-tpl-editor"
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-stone-200 shrink-0">
          <div className="font-serif-dv text-lg">
            {mode === "create" ? "Nueva plantilla" : `Editar "${initial.name}"`}
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700 p-2 -mr-2" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Canal</div>
            <select
              className="dv-input w-full text-sm disabled:opacity-60"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              disabled={!canEditChannel}
              data-testid="msg-tpl-editor-channel"
            >
              {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {!canEditChannel && (
              <div className="text-[11px] text-stone-500 mt-1">El canal no se puede cambiar tras crear la plantilla.</div>
            )}
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Nombre</div>
            <input
              className="dv-input w-full text-sm"
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={120}
              data-testid="msg-tpl-editor-name"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Fase asociada</div>
            <select
              className="dv-input w-full text-sm"
              value={form.stage || ""}
              onChange={(e) => setForm({ ...form, stage: e.target.value || null })}
              data-testid="msg-tpl-editor-stage"
            >
              <option value="">Sin fase (global)</option>
              {STAGE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
              Checklist asociado (opcional)
            </div>
            <input
              className="dv-input w-full text-sm"
              value={form.linked_checklist_key || ""}
              onChange={(e) => setForm({ ...form, linked_checklist_key: e.target.value || null })}
              maxLength={64}
              placeholder="p.ej. first_contact"
              data-testid="msg-tpl-editor-checklist"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
              Texto del mensaje
            </div>
            <textarea
              className="dv-input w-full text-sm font-mono"
              rows={10}
              value={form.body || ""}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              maxLength={4000}
              data-testid="msg-tpl-editor-body"
            />
            <div className="text-[11px] text-stone-500 mt-1">
              Variables disponibles: {"{nombre}"}, {"{telefono}"}, {"{email}"}, {"{canal}"}, {"{pais}"}, {"{interes}"}, {"{villa}"}, {"{precio}"}, {"{link_dossier}"}.
            </div>
          </label>
          {isBuiltin && mode === "edit" && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Esta plantilla es builtin. Puedes editarla, o usar "Restaurar original" desde la tabla para recuperar el texto oficial.
            </div>
          )}
          {localError && (
            <div className="text-xs px-3 py-2 rounded border bg-red-50 border-red-200 text-red-700">{localError}</div>
          )}
        </div>
        <div className="border-t border-stone-200 px-4 sm:px-6 py-3 flex justify-end gap-2 shrink-0">
          <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost text-sm">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="dv-btn dv-btn-primary text-sm disabled:opacity-40"
            data-testid="msg-tpl-editor-save"
          >
            {saving ? "Guardando…" : mode === "create" ? "Crear" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
